import React, { useMemo } from 'react';
import { Box, Typography, Divider, Tooltip, tooltipClasses } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';

import CategoricalBarChart from 'components/CategoricalBarChart';
import CircularLoader from 'components/CircularLoader';

import icn_linebar_chart from 'assets/images/icons/icn_linebar_chart.png';
import { ReactComponent as Clock } from 'assets/images/icons/icn_display_clock.svg';
import icn_info_solid from 'assets/images/icons/icn_Info_solid.svg';

const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#4D4D4D',
    color: '#FFFFFF',
    maxWidth: 550,
    fontSize: theme.typography.pxToRem(12),
    padding: '16px 20px',
    borderRadius: '8px',
    lineHeight: '1.5',
    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.3)',
  },
}));

const TooltipContent = () => (
  <Box>
    <Box sx={{ mb: 2.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Good — Power Consumption &lt; 80% of Budget</Typography>
      <Typography variant="body2" sx={{ fontSize: '12px' }}>
        PoE delivery is stable and well within budget, with headroom for peak power spikes.
      </Typography>
    </Box>

    <Box sx={{ mb: 2.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Warning — 80% ≤ Total Power Consumption &lt; 95%</Typography>
      <Typography variant="body2" sx={{ fontSize: '12px', mb: 1 }}>
        Power consumption is nearing the system budget. New devices may be denied power.
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Recommended Actions:</Typography>
      <Box component="ul" sx={{ pl: 2, m: 0, fontSize: '12px' }}>
        <li>Set PoE Priority so mission-critical ports stay online.</li>
        <li>Redistribute high-power devices or upgrade the switch power supply.</li>
      </Box>
    </Box>

    <Box>
      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Critical — Total Power ≥ 95%</Typography>
      <Typography variant="body2" sx={{ fontSize: '12px', mb: 1 }}>
        Power faults (Short Circuit, Overload, Power Denied) are disrupting connected devices.
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Recommended Actions:</Typography>
      <Box component="ul" sx={{ pl: 2, m: 0, fontSize: '12px' }}>
        <li>Inspect cabling and connectors for damage.</li>
        <li>Verify connected devices match the port's PoE class and the 100m cable limit.</li>
      </Box>
    </Box>
  </Box>
);

const PoEFaultDetection = ({ data, loading, isEmpty, onClick }) => {
  const theme = useTheme();
  const handleOpenDrawer = !isEmpty ? onClick : undefined;

  const { otherErrors } = useMemo(() => {
    if (!data?.errorCounts) return { otherErrors: 0 };
    const { mpsAbsent, notPoePort, overVoltage, startupFailure, thermalShutdown, underVoltage } = data.errorCounts;
    return { otherErrors: mpsAbsent + notPoePort + overVoltage + startupFailure + thermalShutdown + underVoltage };
  }, [data]);

  const totalBudget = data?.totalPower || 0;
  const usedPowerPercent = data && totalBudget > 0 ? Math.round((data.usedPower / totalBudget) * 100) : 0;

  const getStatus = (val) => {
    if (val >= 95) return { label: 'Critical', color: '#F44336' };
    if (val >= 80) return { label: 'Warning', color: '#FF9800' };
    return { label: 'Good', color: '#4CAF50' };
  };

  const status = data ? getStatus(usedPowerPercent) : { label: 'Good', color: '#4CAF50' };

  const chartData = useMemo(() => {
    if (!data) return null;
    return {
      categories: ['Short Circuit', 'Overload', 'Power Denied', 'Other Errors'],
      seriesData: [
        { value: data.errorCounts.pdShort, itemStyle: { color: '#172664' } },
        { value: data.errorCounts.overload, itemStyle: { color: '#4B68CF' } },
        { value: data.errorCounts.powerDenied, itemStyle: { color: '#87A2E2' } },
        { value: otherErrors, itemStyle: { color: '#C9DCF3' } }
      ]
    };
  }, [data, otherErrors]);

  const tooltipOption = useMemo(() => ({
    show: true,
    trigger: 'item',
    backgroundColor: 'rgba(55, 55, 55, 0.9)',
    borderColor: 'transparent',
    padding: [8, 12],
    borderRadius: 4,
    textStyle: { color: '#fff' },
    formatter: (params) => {
      return `
        <div style="font-size: 12px; line-height: 1.6;">
          <div style="opacity: 0.9;">${params.name}</div>
          <div>Occurrences : <span style="font-weight: bold;">${params.value}</span></div>
        </div>
      `;
    }
  }), []);

  return (
    <Box
      sx={{
        border: '1px solid #E2E8F0',
        borderRadius: '12px',
        p: 2,
        bgcolor: 'white',
        mb: 3,
        cursor: isEmpty ? 'default' : 'pointer',
        '&:hover': handleOpenDrawer ? {
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
        } : {}
      }}
      onClick={handleOpenDrawer}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box
            component="img"
            src={icn_linebar_chart}
            sx={{ width: '18px', height: '18px', mr: 1 }}
          />
          <Typography sx={{ fontWeight: 'bold', color: '#172664', fontSize: '15px', mr: 1 }}>
            PoE Status Detection
          </Typography>
          <HtmlTooltip title={<TooltipContent />} placement="auto">
            <Box
              component="img"
              src={icn_info_solid}
              sx={{ width: '16px', height: '16px', cursor: 'pointer' }}
            />
          </HtmlTooltip>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: theme.palette.primary.main,
          }}
        >
          <Clock />
          <Typography
            sx={{
              marginLeft: '5px',
              fontSize: theme.typography.customFontSize[2],
              fontWeight: theme.typography.customFontWeight[2],
            }}
          >
            Last 24 Hours
          </Typography>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ height: '250px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularLoader />
        </Box>
      ) : isEmpty ? (
        <Box sx={{ height: '250px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography sx={{ color: theme.palette.primary.greyThird, fontSize: theme.typography.customFontSize[2] }}>
            No data available
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Left Panel */}
          <Box sx={{ width: { xs: '100%', md: '25%' }, pr: { xs: 0, md: 4 }, mb: { xs: 3, md: 0 } }}>
            <Typography sx={{ color: '#1C1C1E', fontWeight: 'bold', fontSize: '13px', mb: 1 }}>Status</Typography>
            <Typography sx={{ fontSize: '35px', fontWeight: 'bold', color: status.color, lineHeight: 1.2, mb: 2 }}>
              {status.label === 'Warning' ? 'Warn' : status.label}
            </Typography>

            <Box sx={{ width: '100%', borderBottom: '1px solid #E2E8F0', mb: 2 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 'bold', color: '#1C1C1E' }}>Power Consumption:</Typography>
                <Typography sx={{ fontSize: '13px', color: '#4C6EF5', fontWeight: 'bold' }}>
                  {Number(data.usedPower?.toFixed(2))}W({usedPowerPercent}%) / {totalBudget}W
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 'bold', color: '#1C1C1E' }}>Short Circuit:</Typography>
                <Typography sx={{ fontSize: '13px', color: '#4C6EF5', fontWeight: 'bold' }}>{data.errorCounts.pdShort}</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 'bold', color: '#1C1C1E' }}>Overload:</Typography>
                <Typography sx={{ fontSize: '13px', color: '#4C6EF5', fontWeight: 'bold' }}>{data.errorCounts.overload}</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 'bold', color: '#1C1C1E' }}>Power Denied:</Typography>
                <Typography sx={{ fontSize: '13px', color: '#4C6EF5', fontWeight: 'bold' }}>{data.errorCounts.powerDenied}</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 'bold', color: '#1C1C1E' }}>Other Errors:</Typography>
                <Typography sx={{ fontSize: '13px', color: '#4C6EF5', fontWeight: 'bold' }}>{otherErrors}</Typography>
              </Box>
            </Box>
          </Box>

          {/* Vertical Separator */}
          <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' }, mr: 4, borderColor: '#EDF2F7' }} />

          {/* Right Chart */}
          <Box sx={{ flex: 1, width: '100%' }}>
            <CategoricalBarChart chartData={chartData} height="250px" tooltipCustomOption={tooltipOption} />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default PoEFaultDetection;